import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import { PATH } from 'src/features/navigation/utils/constants';
import DeleteValidatorAccount from 'src/features/validatorAccounts/components/forms/DeleteValidatorAccount';
import UpdateValidatorAccountFormModal from 'src/features/validatorAccounts/components/forms/UpdateValidatorAccountFormModal';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import { ValidatorAccount } from 'src/features/validatorAccounts/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

type CellValidatorAccountActionsProps<TData, TValue> = CellContext<TData, TValue>;

export default function CellValidatorAccountsActions({
    row,
}: CellValidatorAccountActionsProps<ValidatorAccount, unknown>) {
    const { formatMessage } = useIntl();
    const permissionManageValidation = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);

    return (
        <div className="flex gap-1">
            <TooltipProvider>
                {permissionManageValidation && (
                    <>
                        <Tooltip>
                            <UpdateValidatorAccountFormModal
                                semantics={{
                                    entryName: formatMessage(validatorAccountsMessages.account).toLowerCase(),
                                }}
                                selectedAccount={row.original}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdateValidatorAccountFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeleteValidatorAccount
                                semantics={{
                                    entryName: formatMessage(validatorAccountsMessages.account).toLowerCase(),
                                }}
                                selectedAccount={row.original}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeleteValidatorAccount>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Link to={`/${PATH.ADMINISTRATION}/validator-accounts/${row.original.id}`}>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
